/**
 * The decisions behind `Combobox`, free of React and Base UI so they can be
 * tested directly: what to list, what is selected, what a value change means
 * and which status line to show. `combobox.tsx` wires them to the parts.
 */

/** Values are strings; these can never collide with a `getValue` result. */
const ACTION_PREFIX = "\u0000combobox:";
export const CREATE = `${ACTION_PREFIX}create`;
export const LOAD_MORE = `${ACTION_PREFIX}load-more`;
export const CLEAR = `${ACTION_PREFIX}clear`;

export const isAction = (value: string) => value.startsWith(ACTION_PREFIX);

/** How close to the end of the list scrolling pulls the next batch in. */
const LOAD_MORE_THRESHOLD_PX = 48;

export type Section<T> = { key: string; label?: string; items: T[] };

export type CheckState = boolean | "indeterminate";

type Filter<T> = (item: T, query: string) => boolean;

export function toSections<T>(source: {
  items?: readonly T[];
  groups?: readonly { label?: string; items: readonly T[] }[];
}): Section<T>[] {
  if (source.groups) {
    return source.groups.map((group, index) => ({
      key: `group:${group.label ?? ""}:${index}`,
      label: group.label,
      items: [...group.items],
    }));
  }
  return [{ key: "items", items: [...(source.items ?? [])] }];
}

/** The selection as values, whichever mode the picker is in. */
export function selectionValues(
  value: string | readonly string[] | null | undefined,
): string[] {
  if (Array.isArray(value)) return [...value];
  return typeof value === "string" && value !== "" ? [value] : [];
}

/** Values resolved to items; values with no item are left out. */
export function resolveItems<T>(
  values: readonly string[],
  lookup: ReadonlyMap<string, T>,
): T[] {
  return values.flatMap((value) => {
    const item = lookup.get(value);
    return item === undefined ? [] : [item];
  });
}

/** Case-insensitive substring of the label or the value. */
export function defaultFilter<T>(
  getLabel: (item: T) => string,
  getValue: (item: T) => string,
): Filter<T> {
  return (item, query) => {
    const needle = query.toLowerCase();
    return (
      getLabel(item).toLowerCase().includes(needle) ||
      getValue(item).toLowerCase().includes(needle)
    );
  };
}

/**
 * The client filter in force: none when the caller filters (`null`) or the
 * server searches, the caller's own, or the default.
 */
export function resolveFilter<T>(
  filter: Filter<T> | null | undefined,
  serverSearch: boolean,
  fallback: () => Filter<T>,
): Filter<T> | null {
  if (filter === null || serverSearch) return null;
  return filter ?? fallback();
}

export function checkState<T>(
  items: readonly T[],
  isSelected: (item: T) => boolean,
): CheckState {
  if (items.length > 0 && items.every(isSelected)) return true;
  return items.some(isSelected) ? "indeterminate" : false;
}

/**
 * The sections to list: filtered by the query and, with `pin`, led by the
 * selection as a section of its own so grouped results can't push it down.
 */
export function visibleSections<T>({
  sections,
  query,
  filter,
  pin,
  selected,
  getValue,
}: {
  sections: Section<T>[];
  query: string;
  filter: Filter<T> | null;
  pin: boolean;
  selected: T[];
  getValue: (item: T) => string;
}): Section<T>[] {
  const matching =
    filter && query
      ? sections.map((section) => ({
          ...section,
          items: section.items.filter((item) => filter(item, query)),
        }))
      : sections;
  if (!pin || selected.length === 0) {
    return matching.filter((section) => section.items.length > 0);
  }
  const pinned = new Set(selected.map(getValue));
  const pinnedSection = { key: "pinned", items: selected };
  const rest = matching.map((section) => ({
    ...section,
    items: section.items.filter((item) => !pinned.has(getValue(item))),
  }));
  // While typing, the selection trails the matches: leading them, it would
  // take the first-row highlight, and Enter would re-pick (or clear) it
  // instead of picking the top match.
  return (query ? [...rest, pinnedSection] : [pinnedSection, ...rest]).filter(
    (section) => section.items.length > 0,
  );
}

/**
 * Whether the query can be offered as a new item: by `isCreatable` when
 * given, otherwise unless a listed label already matches it.
 */
export function canCreate<T>({
  query,
  isCreatable,
  listedItems,
  getLabel,
}: {
  query: string;
  isCreatable: ((query: string) => boolean) | undefined;
  listedItems: readonly T[];
  getLabel: (item: T) => string;
}): boolean {
  if (query === "") return false;
  if (isCreatable) return isCreatable(query);
  const needle = query.toLowerCase();
  return !listedItems.some((item) => getLabel(item).toLowerCase() === needle);
}

/** Every navigable row's value, in render order. */
export function navigableValues({
  listedValues,
  creatable,
  hasMore,
  allowClear,
}: {
  listedValues: readonly string[];
  creatable: boolean;
  hasMore: boolean;
  allowClear: boolean;
}): string[] {
  return [
    ...listedValues,
    ...(creatable ? [CREATE] : []),
    ...(hasMore ? [LOAD_MORE] : []),
    ...(allowClear ? [CLEAR] : []),
  ];
}

export type ValueChange =
  { kind: "action"; action: string } | { kind: "values"; values: string[] };

/**
 * What a Base UI value change means: an action row was picked, or the
 * selection becomes `values`. Re-picking the single selection clears it when
 * the picker is `deselectable`.
 */
export function decideValueChange(
  next: string | readonly string[] | null,
  current: readonly string[],
  deselectable: boolean,
): ValueChange {
  const nextValues =
    next === null ? [] : typeof next === "string" ? [next] : [...next];
  const action = nextValues.find(isAction);
  if (action) return { kind: "action", action };
  if (deselectable && typeof next === "string" && next === current[0]) {
    return { kind: "values", values: [] };
  }
  return { kind: "values", values: nextValues };
}

/** Select every one of `itemValues`, or deselect them all if all are. */
export function toggleAllValues(
  values: readonly string[],
  itemValues: readonly string[],
): string[] {
  const selected = new Set(values);
  if (itemValues.every((value) => selected.has(value))) {
    const toggled = new Set(itemValues);
    return values.filter((value) => !toggled.has(value));
  }
  return [...values, ...itemValues.filter((value) => !selected.has(value))];
}

/** Whether a scroll position is close enough to the end to load more. */
export function nearListEnd({
  scrollTop,
  scrollHeight,
  clientHeight,
}: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}): boolean {
  return scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX;
}

export type StatusKind = "error" | "loading" | "empty" | null;

/** The one status line the list shows, if any. */
export function statusKind({
  error,
  loading,
  listedCount,
  creatable,
}: {
  error: boolean;
  loading: boolean;
  listedCount: number;
  creatable: boolean;
}): StatusKind {
  if (error) return "error";
  if (listedCount > 0) return null;
  if (loading) return "loading";
  return creatable ? null : "empty";
}
